import {
  FETCH_PAYMENT_FAILURE,
  FETCH_PAYMENT_REQUEST,
  FETCH_PAYMENT_SUCCESS,
} from 'constants/payment';
import { queryKeys } from 'hooks/queryKeys';
import type { Payment } from 'models/payment';
import {
  type OrderService,
  orders as ordersService,
} from 'services/apiV3/orders/orders';
import {
  type PaymentsService,
  payments as paymentsService,
} from 'services/payex/payments/payments';
import { normalizePayment } from 'services/payments/payments';
import type { PayexDispatch } from 'store/configureStore';

type ByOrderParams = {
  orderId: string;
  token: string;
  locale: string;
};

type ByOrderDependencies = {
  dispatch: PayexDispatch;
  orders?: Pick<OrderService, 'fetchPayment'>;
};

type ByIdParams = {
  id: string;
  locale: string;
};

const PAYMENT_NOT_FOUND = 'Payment not found';

export const paymentQueries = {
  byOrder: (
    { orderId, token, locale }: ByOrderParams,
    { dispatch, orders = ordersService }: ByOrderDependencies,
  ) => ({
    queryKey: queryKeys.payment.byOrder(orderId, locale),
    queryFn: async (): Promise<Payment> => {
      dispatch({ type: FETCH_PAYMENT_REQUEST });

      try {
        const response = await orders.fetchPayment({ orderId, token, locale });
        const payment = response[response.length - 1];

        if (!payment) {
          throw new Error(PAYMENT_NOT_FOUND);
        }

        dispatch({
          type: FETCH_PAYMENT_SUCCESS,
          payload: normalizePayment(payment),
        });

        return payment;
      } catch (error) {
        dispatch({ type: FETCH_PAYMENT_FAILURE });
        throw error;
      }
    },
    staleTime: 0,
    retry: false,
  }),
  byId: (
    { id, locale }: ByIdParams,
    payments: Pick<PaymentsService, 'fetchById'> = paymentsService,
  ) => ({
    queryKey: queryKeys.payment.byId(id, locale),
    queryFn: async (): Promise<Payment> => {
      const payment = await payments.fetchById({ id, locale });

      if (!payment) {
        throw new Error(PAYMENT_NOT_FOUND);
      }

      return payment;
    },
    staleTime: 0,
    retry: false,
  }),
};
